import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import TableLoader from "../../../shared/TableLoader";
import NoDataFound from "../../../shared/NoDataFound";
import Select from "react-select";
import { CSVLink } from "react-csv";
import { BrandsTypes, toLakhs } from "../../../utils/constants";
import BrandDetails from "./BrandDetails";

const ReviewReport = () => {
  const now = new Date();
  const initialSelectedMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [selectedMonth, setSelectedMonth] = useState(initialSelectedMonth);
  const baseYear = selectedMonth.getFullYear();
  const prevYear = baseYear - 1;
  const [brandType, setBrandType] = useState(2);
  const [reportData, setReportData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const reviewReportsBase = "/review-reports";
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [showBrandDetails, setShowBrandDetails] = useState(false);
  const [brandDetailsUrl, setBrandDetailsUrl] = useState("");
  const [brandDetailsPayload, setBrandDetailsPayload] = useState(null);
  const [brandDetailsTitle, setBrandDetailsTitle] = useState("");

  const selectedMonthLabel = useMemo(() => {
    if (!selectedMonth) return "";
    return selectedMonth.toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });
  }, [selectedMonth]);

  const minSelectableMonth = useMemo(() => new Date(2025, 11, 1), []);
  const maxSelectableMonth = useMemo(() => {
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth()+1, 0);
    lastMonthEnd.setHours(23, 59, 59, 999);
    return lastMonthEnd;
  }, [now]);

  const rows = [
    { type: "section", label: "Total Revenues" },
    { key: "eenadu_customers", label: "Eenadu Accounts", detailUrl: "/review-reports/convertedBrandWiseDetails", newspaper:'Eenadu' },
    { key: "eenadu_revenues", label: "Eenadu Revenues " },
    { key: "eenadu_insertions", label: "Eenadu Insertions" },
    { key: "w2n_customers", label: "W2N Accounts", detailUrl: "/review-reports/convertedBrandWiseDetails", newspaper:'Way2news' },
    { key: "w2n_revenues", label: "W2N Revenues " },
    { key: "w2n_insertions", label: "W2N Insertions" },
    { type: "section", label: "Exclusive Eenadu Accounts" },
    {
      key: "exclusive_eenadu_customers",
      label: "No of Accounts",
      detailUrl: "/review-reports/nonConvertedBrandWiseDetails",
    },
    { key: "exclusive_eenadu_revenues", label: "Revenues " },
    { key: "exclusive_eenadu_insertions", label: "Insertions" },
    { type: "section", label: "Converted Accounts" },
    {
      key: "converted_customers",
      label: "No of Accounts",
      detailUrl: "/review-reports/convertedCommonBrandWiseDetails",
    },
    {
      key: "customer_conversion_pct",
      label: "% customer conversion",
      format: "percent",
    },
    {
      key: "eenadu_converted_revenues",
      label: "Eenadu revenues - converted Accounts ",
    },
    {
      key: "eenadu_converted_insertions",
      label: "Eenadu Insertions - converted Accounts",
    },
    {
      key: "w2n_converted_revenues",
      label: "W2N revenues - converted Accounts ",
    },
    {
      key: "w2n_converted_insertions",
      label: "W2N Insertions - converted Accounts",
    },
    {
      key: "revenue_extraction_pct",
      label: "% revenue extraction",
      format: "percent",
    },
    { type: "section", label: "Exclusive W2N Accounts" },
    {
      key: "exclusive_w2n_customers",
      label: "No of Accounts",
      detailUrl: "/review-reports/exclusiveBrandWiseDetails",
    },
    { key: "exclusive_w2n_revenues", label: "Revenues " },
    { key: "exclusive_w2n_insertions", label: "Insertions" },
    {
      key: "exclusive_revenues_contribution_pct",
      label: "Exclusive revenues % contribution",
      format: "percent",
    },
    { type: "section", label: "Customer contact" },
    {
      key: "w2n_deployed_customers",
      label: "W2N Deployed Accounts",
      onlyMonth: true,
    },
    {
      key: "customers_visited_pct",
      label: "% Accounts visited in that period",
      format: "percent",
      onlyMonth: true,
    },
    {
      key: "eenadu_customers_not_met",
      label: "No of Eenadu Accounts not met in the period",
      detailUrl: "/review-reports/convertedBrandIDWiseDetails",
      onlyMonth: true,
    },
    {
      key: "unknown_missings_pct",
      label: "% Unknown missings",
      format: "percent",
      onlyMonth: true,
    },
  ];


  useEffect(() => {
    const fetchReviewReport = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: `${reviewReportsBase}/getReviewReport`,
          method: "POST",
          data: {
            selectedMonth,
            currentYear: initialSelectedMonth.getFullYear(),
            lastYear: initialSelectedMonth.getFullYear()-1,
            brand_type: brandType,
          },
        });

        if (result.status === "SUCCESS") {
          if (Array.isArray(result.data)) {
            setReportData(result.data);
          } else {
            setReportData(result.data || {});
          }
        } else {
          setReportData({});
          Toast("error", result?.message || "Failed To Fetch Review Report");
        }
      } catch (error) {
        setReportData({});
        Toast("error", "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedMonth) {
      fetchReviewReport();
    }
  }, [selectedMonth, baseYear, prevYear, brandType]);

  const getCellValue = (rowKey, columnKey, format) => {
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    if (format === "percent") {
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) return raw;
      return `${numeric}%`;
    }
    if (rowKey.includes("revenues")) {
      return toLakhs(raw);
    }
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const parseNumeric = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      if (cleaned === "") return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getRevenueTitle = (rowKey, columnKey) => {
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    const parsed = parseNumeric(raw);
    return parsed === null ? raw : parsed;
  };

  const getCsvValue = (rowKey, columnKey, format) => {
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    if (format === "percent") {
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) return raw;
      return `${numeric}%`;
    }
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const getCellClass = (columnKey, onlyMonth) => {
    const base = "border px-2 py-1 text-center";
    const groupBorder =
      columnKey === "month_current" || columnKey === "ytd_current"
        ? "border-l-2 border-gray-500"
        : "";
    const monthOnlyMuted =
      onlyMonth && (columnKey === "year_total" || columnKey.startsWith("ytd_"))
        ? "bg-gray-300 text-gray-600"
        : "";
    return `${base} ${groupBorder} ${monthOnlyMuted}`.trim();
  };

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDateRangeForColumn = (columnKey) => {
    if (!selectedMonth) return null;
    const baseDate = new Date(selectedMonth);
    const year = baseDate.getFullYear();
    const monthIndex = baseDate.getMonth();
    if (columnKey === "year_total") {
      return {
        startDate: formatDate(new Date(year - 1, 0, 1)),
        endDate: formatDate(new Date(year - 1, 11, 31)),
      };
    }
    if (columnKey === "month_current") {
      return {
        startDate: formatDate(new Date(year, monthIndex, 1)),
        endDate: formatDate(new Date(year, monthIndex + 1, 0)),
      };
    }
    if (columnKey === "month_last") {
      return {
        startDate: formatDate(new Date(year - 1, monthIndex, 1)),
        endDate: formatDate(new Date(year - 1, monthIndex + 1, 0)),
      };
    }
    if (columnKey === "ytd_current") {
      return {
        startDate: formatDate(new Date(year, 0, 1)),
        endDate: formatDate(new Date(year, monthIndex + 1, 0)),
      };
    }
    if (columnKey === "ytd_last") {
      return {
        startDate: formatDate(new Date(year - 1, 0, 1)),
        endDate: formatDate(new Date(year - 1, monthIndex + 1, 0)),
      };
    }
    return null;
  };

  const handleOpenBrandDetails = (row, columnKey) => {
    if (!row?.detailUrl) return;
    const dateRange = getDateRangeForColumn(columnKey);
    if (!dateRange) return;
    setBrandDetailsUrl(row.detailUrl);
    setBrandDetailsPayload({
      ...dateRange,
      brand_type: brandType,
      newspaper_name:row.newspaper || undefined,
      brand_ids : columnKey === 'month_current' ? reportData?.not_met_ids?.month_current : reportData?.not_met_ids?.month_last
    });
    console.log(columnKey)
    setBrandDetailsTitle(`${row.label} - ${selectedMonthLabel}`);
    setShowBrandDetails(true);
  };

  const handlePrepareCSV = () => {
    if (!reportData || Object.keys(reportData).length === 0) return;
    const rowsForCsv = rows
      .filter((row) => row.key)
      .map((row) => ({
        Metric: row.label,
        [`Jan-Dec ${prevYear}`]: row.onlyMonth
          ? "-"
          : getCsvValue(row.key, "year_total", row.format),
        [`That month ${baseYear}`]: getCsvValue(row.key, "month_current", row.format),
        [`That month ${prevYear}`]: getCsvValue(row.key, "month_last", row.format),
        [`YTD ${baseYear}`]: row.onlyMonth
          ? "-"
          : getCsvValue(row.key, "ytd_current", row.format),
        [`YTD ${prevYear}`]: row.onlyMonth
          ? "-"
          : getCsvValue(row.key, "ytd_last", row.format),
      }));

    setCsvData(rowsForCsv);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-700">
          Selected Month: <span className="font-semibold">{selectedMonthLabel}</span>
        </div>
        <aside className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 min-w-[240px]">
            <span className="font-semibold whitespace-nowrap">Brand Type:</span>
            <Select
              className="min-w-[200px]"
              placeholder="Select Brand Type"
              value={BrandsTypes.find((option) => option.value === brandType) || null}
              onChange={(selected) => setBrandType(selected ? selected.value : null)}
              options={BrandsTypes}
              isClearable
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold whitespace-nowrap">Select Month:</span>
            <DatePicker
              selected={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              minDate={minSelectableMonth}
              maxDate={maxSelectableMonth}
            />
          </div>
          <CSVLink
            data={csvData}
            filename="review_report.csv"
            className="hidden"
            ref={csvLinkRef}
          />
          {Object.keys(reportData || {}).length > 0 && !isLoading && (
            <button
              onClick={handlePrepareCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Download Report
            </button>
          )}
        </aside>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[980px] w-full border border-gray-300 text-sm bg-white">
          <thead>
            <tr className="bg-gray-100 text-center">
              <th className="border px-2 py-2 text-left" rowSpan={2}></th>
              <th className="border px-2 py-2" rowSpan={2}>
                {`Jan-Dec ${initialSelectedMonth.getFullYear()-1}`}
              </th>
              <th className="border px-2 py-2 border-l-2 border-gray-500" colSpan={2}>
                That month
              </th>
              <th className="border px-2 py-2 border-l-2 border-gray-500" colSpan={2}>
                YTD
              </th>
            </tr>
            <tr className="bg-gray-50 text-center">
              <th className="border px-2 py-2 border-l-2 border-gray-500">{baseYear}</th>
              <th className="border px-2 py-2">{prevYear}</th>
              <th className="border px-2 py-2 border-l-2 border-gray-500">{baseYear}</th>
              <th className="border px-2 py-2">{prevYear}</th>
            </tr>
          </thead>
          {isLoading ? (
            <TableLoader colspan="6" />
          ) : Object.keys(reportData || {}).length === 0 ? (
            <NoDataFound colspan="6" />
          ) : (
            <tbody>
              {rows.map((row, index) => {
                if (row.type === "section") {
                  return (
                    <tr key={`section-${index}`} className="bg-gray-100 font-semibold">
                      <td className="border px-2 py-1 text-left">{row.label}</td>
                      <td className="border px-2 py-1"></td>
                      <td className="border px-2 py-1 border-l-2 border-gray-500"></td>
                      <td className="border px-2 py-1"></td>
                      <td className="border px-2 py-1 border-l-2 border-gray-500"></td>
                      <td className="border px-2 py-1"></td>
                    </tr>
                  );
                }

                return (
                  <tr key={row.key}>
                    <td className="border px-2 py-1 text-left">{row.label}</td>
                    {["year_total", "month_current", "month_last", "ytd_current", "ytd_last"].map(
                      (columnKey) => {
                        const value =
                          row.onlyMonth && (columnKey === "year_total" || columnKey.startsWith("ytd_"))
                            ? "-"
                            : getCellValue(row.key, columnKey, row.format);
                        const isClickable = Boolean(row.detailUrl) && value !== "-" && value !== "";
                        const showRevenueTitle = row.key.includes("revenues") && row.format !== "percent";
                        return (
                          <td
                            key={`${row.key}-${columnKey}`}
                            className={`${getCellClass(columnKey, row.onlyMonth)} ${
                              isClickable ? "text-blue-600  cursor-pointer" : ""
                            }`.trim()}
                            onClick={() => isClickable && handleOpenBrandDetails(row, columnKey)}
                            title={showRevenueTitle ? getRevenueTitle(row.key, columnKey) : undefined}
                          >
                            {value}
                          </td>
                        );
                      }
                    )}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      <BrandDetails
        open={showBrandDetails}
        onClose={() => setShowBrandDetails(false)}
        url={brandDetailsUrl}
        payload={brandDetailsPayload}
        title={brandDetailsTitle}
      />
    </div>
  );
};

export default ReviewReport;
